import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { createFacility } from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import {
  ArrowLeft,
  MapPin,
  Building2,
  Wifi,
  Car,
  FileText,
  Tag,
  Image,
  Upload,
  Save,
  Eye,
  AlertCircle,
  Coffee,
  Monitor,
  Users,
  Clock,
  Calendar,
  Settings,
  CheckCircle,
  HelpCircle,
  Phone,
} from "lucide-react";
import {
  IndividualEditPageLayout,
  SidebarSection,
  ActionButtons,
  ActionButton,
  ActionButtonSecondary,
} from "../../../components/layout";
import { useLocationContext } from "../../../client/contexts/LocationContext";

// Tooltip component (reusable)
const Tooltip = ({
  children,
  content,
}: {
  children: React.ReactNode;
  content: string;
}) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        className="cursor-help"
      >
        {children}
      </div>
      {isVisible && (
        <div className="absolute z-10 px-3 py-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64">
          {content}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
        </div>
      )}
    </div>
  );
};

interface FacilityFormData {
  title: string;
  description: string;
  contentBody: string;
  category: string;
  facilityType: string;
  tags: string;
  images: string[];
  isPinned: boolean;
  expiryDate: string;
  isAvailable: boolean;
  reservationRequired: boolean;
  operatingHours: string;
  location: string;
  contact: string;
  isActive: boolean;
}

interface FacilityType {
  value: string;
  label: string;
  icon: any;
  description: string;
  canReserve: boolean;
  color: string;
}

const FACILITY_TYPES: FacilityType[] = [
  {
    value: "vending",
    label: "Vending Machine",
    icon: Coffee,
    description: "Snack and beverage vending machines",
    canReserve: false,
    color: "bg-orange-50 border-orange-200 hover:bg-orange-100",
  },
  {
    value: "equipment",
    label: "Equipment",
    icon: Monitor,
    description: "Computers, printers, projectors, and other equipment",
    canReserve: true,
    color: "bg-blue-50 border-blue-200 hover:bg-blue-100",
  },
  {
    value: "rooms",
    label: "Meeting Room",
    icon: Building2,
    description: "Conference rooms, meeting spaces, and private rooms",
    canReserve: true,
    color: "bg-purple-50 border-purple-200 hover:bg-purple-100",
  },
  {
    value: "coworking",
    label: "Co-Working Space",
    icon: Users,
    description: "Shared workspaces and collaborative areas",
    canReserve: true,
    color: "bg-green-50 border-green-200 hover:bg-green-100",
  },
  {
    value: "parking",
    label: "Parking Space",
    icon: Car,
    description: "Parking spots and vehicle storage",
    canReserve: true,
    color: "bg-yellow-50 border-yellow-200 hover:bg-yellow-100",
  },
  {
    value: "wifi",
    label: "WiFi Hotspot",
    icon: Wifi,
    description: "Internet access points and connectivity areas",
    canReserve: false,
    color: "bg-indigo-50 border-indigo-200 hover:bg-indigo-100",
  },
];

const FACILITY_CATEGORIES = [
  { value: "vending", label: "Vending Machines", icon: Coffee },
  { value: "equipment", label: "Equipment", icon: Monitor },
  { value: "rooms", label: "Rooms", icon: Building2 },
  { value: "coworking", label: "Co-Working Spaces", icon: Users },
  { value: "parking", label: "Parking", icon: Car },
  { value: "wifi", label: "WiFi", icon: Wifi },
  { value: "general", label: "General", icon: MapPin },
];

const FACILITY_ACCESS_TYPES = [
  {
    value: "available",
    label: "Available",
    description: "Freely available for use",
  },
  {
    value: "reservable",
    label: "Reservable",
    description: "Requires advance reservation",
  },
  {
    value: "walkin",
    label: "Walk-in Only",
    description: "First come, first served",
  },
];



export default function AddFacilityPage() {
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Get the selected facility type from URL params
  const selectedTypeParam = searchParams.get("type");
  const [selectedFacilityType, setSelectedFacilityType] =
    useState<FacilityType | null>(null);

  const [formData, setFormData] = useState<FacilityFormData>({
    title: "",
    description: "",
    contentBody: "",
    category: "general",
    facilityType: "available",
    tags: "",
    images: [],
    isPinned: false,
    expiryDate: "",
    isAvailable: true,
    reservationRequired: false,
    operatingHours: "",
    location: "",
    contact: "",
    isActive: true,
  });

  // Initialize selected facility type from URL params
  useEffect(() => {
    if (selectedTypeParam) {
      const type = FACILITY_TYPES.find((t) => t.value === selectedTypeParam);
      if (type) {
        setSelectedFacilityType(type);
        setFormData((prev) => ({
          ...prev,
          category: type.value,
          reservationRequired: type.canReserve,
        }));
      }
    }
  }, [selectedTypeParam]);

  // Helper function to get placeholder text based on facility type
  const getPlaceholderForType = (type: string): string => {
    switch (type) {
      case "vending":
        return "e.g., Snack Vending Machine - Level 2, Coffee Machine - Lobby";
      case "equipment":
        return "e.g., 3D Printer - Engineering Lab, Projector - Conference Room A";
      case "rooms":
        return "e.g., Conference Room A - Level 3, Meeting Room B - Level 1";
      case "coworking":
        return "e.g., Open Workspace - Level 2, Quiet Zone - Level 4";
      case "parking":
        return "e.g., Parking Spot #15 - Level B1, Visitor Parking - Ground Floor";
      case "wifi":
        return "e.g., WiFi Hotspot - Lobby, Guest Network - Level 2";
      default:
        return "e.g., Facility Name - Location";
    }
  };

  const handleChange = (field: keyof FacilityFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    }

    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
    }

    if (!selectedLocation?.id) {
      newErrors.location = "No location selected";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const facilityData = {
        locationId: selectedLocation!.id,
        title: formData.title.trim(),
        description: formData.description.trim(),
        contentBody: formData.contentBody.trim(),
        category: formData.category,
        facilityType: formData.facilityType,
        tags: formData.tags
          .split(",")
          .map((tag) => tag.trim())
          .filter(Boolean),
        images: formData.images,
        isPinned: formData.isPinned,
        expiryDate: formData.expiryDate
          ? new Date(formData.expiryDate)
          : undefined,
        metadata: {
          facilityType: formData.facilityType,
          isAvailable: formData.isAvailable,
          reservationRequired: formData.reservationRequired,
          operatingHours: formData.operatingHours,
          facilityLocation: formData.location,
          contact: formData.contact,
          isActive: formData.isActive,
        },
      };

      await createFacility(facilityData);

      // Navigate back to facilities page
      if (selectedLocation?.locationCode) {
        navigate(`/locations/${selectedLocation.locationCode}/facilities`);
      } else {
        navigate("/facilities");
      }
    } catch (error) {
      console.error("Error creating facility:", error);
      setErrors({ submit: "Failed to create facility. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTypeSelection = (type: FacilityType) => {
    setSelectedFacilityType(type);
    setFormData((prev) => ({
      ...prev,
      category: type.value,
      reservationRequired: type.canReserve,
    }));
  };

  const handleCancel = () => {
    if (selectedLocation?.locationCode) {
      navigate(`/locations/${selectedLocation.locationCode}/facilities`);
    } else {
      navigate("/facilities");
    }
  };

  if (!user) {
    return (
      <IndividualEditPageLayout title="Access Denied" icon={AlertCircle}>
        <div className="p-6 text-center py-12">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Access Denied
          </h3>
          <p className="text-gray-600">Please log in to add a facility.</p>
        </div>
      </IndividualEditPageLayout>
    );
  }

  if (!selectedLocation) {
    return (
      <IndividualEditPageLayout title="No Location" icon={AlertCircle}>
        <div className="p-6 text-center py-12">
          <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Location Selected
          </h3>
          <p className="text-gray-600">
            Please select a location to add a facility.
          </p>
        </div>
      </IndividualEditPageLayout>
    );
  }

  // If no facility type is selected, show type selection
  if (!selectedFacilityType) {
    return (
      <IndividualEditPageLayout 
        title="Add New Facility" 
        subtitle={`Location: ${selectedLocation?.name || ""}`}
        icon={Building2}
      >
        <div className="max-w-4xl mx-auto py-8">
          {/* Instructions */}
          <div className="mb-8">
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6">
              <h2 className="text-lg font-semibold text-blue-900 mb-2">
                Select Facility Type
              </h2>
              <p className="text-blue-800">
                Choose the type of facility you want to add. Each type has
                different features and reservation capabilities.
              </p>
            </div>
          </div>

          {/* Facility Type Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {FACILITY_TYPES.map((type) => {
              const IconComponent = type.icon;
              return (
                <button
                  key={type.value}
                  onClick={() => handleTypeSelection(type)}
                  className={`${type.color} border-2 rounded-2xl p-6 text-left transition-all duration-200 hover:shadow-lg hover:scale-[1.02] group`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 bg-white rounded-xl shadow-sm group-hover:shadow-md transition-shadow">
                      <IconComponent className="w-6 h-6 text-gray-700" />
                    </div>
                    {type.canReserve && (
                      <div className="flex items-center gap-1 text-xs text-green-600 bg-green-100 px-2.5 py-1 rounded-full font-bold">
                        <Calendar className="w-3.5 h-3.5" />
                        Reservable
                      </div>
                    )}
                  </div>
 
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {type.label}
                  </h3>
 
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                    {type.description}
                  </p>
 
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      {type.canReserve ? (
                        <>
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span>Can be reserved</span>
                        </>
                      ) : (
                        <>
                          <Clock className="w-4 h-4 text-orange-500" />
                          <span>Walk-in only</span>
                        </>
                      )}
                    </div>
                    <div className="text-blue-600 group-hover:text-blue-700 font-bold">
                      Select →
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
 
          {/* Help Text */}
          <div className="mt-12 text-center">
            <p className="text-sm text-gray-500 border-t border-slate-100 pt-8">
              Need help choosing? Contact your location administrator for
              guidance on facility types and requirements.
            </p>
          </div>
        </div>
      </IndividualEditPageLayout>
    );
  }

  const actionsContent = (
    <ActionButtons>
      <ActionButton
        type="submit"
        form="facility-form"
        disabled={isSubmitting || !formData.title.trim()}
        icon={Save}
      >
        {isSubmitting ? "Adding..." : "Add Facility"}
      </ActionButton>
      <ActionButtonSecondary onClick={handleCancel} disabled={isSubmitting}>
        Cancel
      </ActionButtonSecondary>
    </ActionButtons>
  );

  const sidebarContent = (
    <>
      <SidebarSection title="Facility Status" hideOnMobile>
        <div className="space-y-4">
          <label className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-xl cursor-pointer hover:bg-slate-100 transition-colors">
            <input
              type="checkbox"
              checked={formData.isAvailable}
              onChange={(e) => handleChange("isAvailable", e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
            />
            <span className="text-sm font-bold text-slate-700 flex-1">Currently Available</span>
            <Tooltip content="Facility is currently available for use">
              <HelpCircle className="w-4 h-4 text-gray-400" />
            </Tooltip>
          </label>

          <label className={`flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-xl transition-colors ${!selectedFacilityType.canReserve ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:bg-slate-100"}`}>
            <input
              type="checkbox"
              checked={formData.reservationRequired}
              onChange={(e) => handleChange("reservationRequired", e.target.checked)}
              disabled={!selectedFacilityType.canReserve}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
            />
            <span className="text-sm font-bold text-slate-700 flex-1">Reservation Required</span>
            <Tooltip content={selectedFacilityType.canReserve ? "Users must reserve in advance" : "Cannot be reserved"}>
              <HelpCircle className="w-4 h-4 text-gray-400" />
            </Tooltip>
          </label>
        </div>
      </SidebarSection>

      <SidebarSection title="Display Options" hideOnMobile>
        <div className="space-y-4">
          <label className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-xl cursor-pointer hover:bg-slate-100 transition-colors">
            <input
              type="checkbox"
              checked={formData.isPinned}
              onChange={(e) => handleChange("isPinned", e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
            />
            <span className="text-sm font-bold text-slate-700">Pin to Top</span>
          </label>
          <label className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-xl cursor-pointer hover:bg-slate-100 transition-colors">
            <input
              type="checkbox"
              checked={formData.isActive}
              onChange={(e) => handleChange("isActive", e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
            />
            <span className="text-sm font-bold text-slate-700">Active Listing</span>
          </label>
        </div>
      </SidebarSection>
    </>
  );

  return (
    <IndividualEditPageLayout
      title={`Add ${selectedFacilityType.label}`}
      subtitle={`Location: ${selectedLocation?.name || ""}`}
      icon={selectedFacilityType.icon}
      actions={actionsContent}
      sidebar={sidebarContent}
    >
      <form id="facility-form" onSubmit={handleSubmit} className="space-y-8">
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm space-y-6">
          <div>
            <label className="block text-sm font-semibold text-slate-900 mb-2 uppercase tracking-tight">
              What {selectedFacilityType.label.toLowerCase()} are you adding?
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleChange("title", e.target.value)}
              className={`w-full px-4 py-3 bg-slate-50 border rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-medium ${errors.title ? "border-red-300 bg-red-50" : "border-slate-200"}`}
              placeholder={getPlaceholderForType(selectedFacilityType.value)}
              required
            />
            {errors.title && (
              <p className="mt-1 text-sm text-red-600 flex items-center gap-1 font-medium">
                <AlertCircle className="w-4 h-4" /> {errors.title}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-900 mb-2 uppercase tracking-tight">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              rows={4}
              className={`w-full px-4 py-3 bg-slate-50 border rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-medium ${errors.description ? "border-red-300 bg-red-50" : "border-slate-200"}`}
              placeholder="Brief description of features, purpose, etc."
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-6">
            <h3 className="font-bold text-slate-900 flex items-center gap-2 px-1"><Clock className="w-4 h-4 text-blue-600" /> Availability</h3>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Access Type</label>
                <select
                  value={formData.facilityType}
                  onChange={(e) => handleChange("facilityType", e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all font-medium"
                >
                  {FACILITY_ACCESS_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Operating Hours</label>
                <input
                  type="text"
                  value={formData.operatingHours}
                  onChange={(e) => handleChange("operatingHours", e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all font-medium"
                  placeholder="e.g., Mon-Fri: 9AM - 5PM"
                />
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <h3 className="font-bold text-slate-900 flex items-center gap-2 px-1"><MapPin className="w-4 h-4 text-blue-600" /> Location & Tags</h3>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Specific Location</label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => handleChange("location", e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all font-medium"
                  placeholder="e.g., Level 2, Room 204"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Tags (comma separated)</label>
                <input
                  type="text"
                  value={formData.tags}
                  onChange={(e) => handleChange("tags", e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all font-medium"
                  placeholder="e.g., wifi, quiet, outlets"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="font-bold text-slate-900 flex items-center gap-2 px-1"><Phone className="w-4 h-4 text-blue-600" /> Contact Info</h3>
          <input
            type="text"
            value={formData.contact}
            onChange={(e) => handleChange("contact", e.target.value)}
            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all font-medium"
            placeholder="Phone, email, or Slack handle"
          />
        </div>

        {errors.submit && <div className="p-4 bg-red-50 border border-red-100 rounded-xl text-red-700 text-sm font-medium">{errors.submit}</div>}
      </form>
    </IndividualEditPageLayout>
  );
}
